export const ERROR_CODES_FEDEX = ["DE", "DD", "SE", "CD"];
export const LABEL_PRINTED_CODE_FEDEX = "OC";
export const PICKUP_CODE_FEDEX = "PU";
export const DELIVERED_CODE_FEDEX = "DL";
export const OUT_FOR_DELIVERY_CODE_FEDEX = "OD";
export const WARNING_CODE_FEDEX = ["DF"];

// #region Tracking
// a lot of it is generated by AI just to be able to read the tracking response from Fedex API
type LocationType =
  | "AIRPORT"
  | "CUSTOMS_BROKER"
  | "CUSTOMER"
  | "DELIVERY_LOCATION"
  | "DESTINATION_AIRPORT"
  | "DROP_BOX"
  | "DESTINATION_FEDEX_FACILITY"
  | "ENROUTE"
  | "FEDEX_FACILITY"
  | "INTERLINE_CARRIER"
  | "FEDEX_OFFICE_LOCATION"
  | "NON_FEDEX_FACILITY"
  | "ORIGIN_AIRPORT"
  | "ORIGIN_FEDEX_FACILITY"
  | "PORT_OF_ENTRY"
  | "PICKUP_LOCATION"
  | "PLANE"
  | "SORT_FACILITY"
  | "SHIP_AND_GET_LOCATION"
  | "TURNPOINT"
  | "VEHICLE";

type DelayType = "WEATHER" | "OPERATIONAL" | "LOCAL" | "GENERAL" | "CLEARANCE";

type DelayStatus = "DELAYED" | "ON_TIME" | "EARLY";

type AddressClassification = "BUSINESS" | "RESIDENTIAL" | "MIXED" | "UNKNOWN";

type CarrierCode =
  | "FDXE"
  | "FDXG"
  | "FXSP"
  | "FXFR"
  | "FDXC"
  | "FXCC"
  | "FEDEX_CARGO"
  | "FEDEX_CUSTOM_CRITICAL"
  | "FEDEX_EXPRESS"
  | "FEDEX_FREIGHT"
  | "FEDEX_GROUND"
  | "FEDEX_OFFICE"
  | "FEDEX_KINKOS"
  | "FX"
  | "FDFR"
  | "FDEG"
  | "FXK"
  | "FDC"
  | "FDCC";

type DateTimeType =
  | "ACTUAL_DELIVERY"
  | "ACTUAL_PICKUP"
  | "ACTUAL_TENDER"
  | "ANTICIPATED_TENDER"
  | "APPOINTMENT_DELIVERY"
  | "ATTEMPTED_DELIVERY"
  | "COMMITMENT"
  | "ESTIMATED_ARRIVAL_AT_GATEWAY"
  | "ESTIMATED_DELIVERY"
  | "ESTIMATED_PICKUP"
  | "ESTIMATED_RETURN_TO_STATION"
  | "SHIP"
  | "SHIPMENT_DATA_RECEIVED";

export interface ScanLocation {
  /**
   * Specifies the FedEx classification type for an address.
   * Valid values are BUSINESS, RESIDENTIAL, MIXED, UNKNOWN.
   * Example: BUSINESS
   */
  addressClassification?: AddressClassification;

  /**
   * Placeholder to indicate whether the address is residential (as opposed to commercial).
   */
  residential?: boolean;

  /**
   * Combination of number, street name, etc. At least one line is required for a valid physical address; empty lines should not be included.
   * Example: ["1043 North Easy Street", "Suite 999"].
   * Note: Street lines is shown in response only in secured flow. For non secured flow, street lines is not shown in the response.
   */
  streetLines?: string[];

  /**
   * The name of the city, town, etc.
   * Example: SEATTLE
   */
  city?: string;

  /**
   * This is a placeholder for State or Province code.
   * Example: CA
   */
  stateOrProvinceCode?: string;

  /**
   * Placeholder to specify postal code for the address. Postal Code is required for postal-aware countries.
   * Example: 98101
   */
  postalCode?: string;

  /**
   * Placeholder for country code (2 characters) for the address.
   * Example: US
   */
  countryCode?: string;

  /**
   * Field holds the fully spelled out name of a country.
   * Example: United States
   */
  countryName?: string;
}

export interface DelayDetail {
  /**
   * Specifies the type of delay.
   */
  type: DelayType;

  /**
   * Specifies the subType of delay.
   * Example: SNOW, TORNADO, EARTHQUAKE, etc.
   */
  subType: string;

  /**
   * Specifies the status of package indicating whether a package is arriving early or is on time or has been delayed.
   */
  status: DelayStatus;
}

export interface FedexScanEvent {
  /**
   * Date and time of the scan event.
   * Example: '2018-02-02T12:01:00-07:00'
   */
  date: string;

  /**
   * Field which holds status description of the track information for the scan event.
   * Example: 'Picked Up'
   */
  derivedStatus: string;

  /**
   * Location Details for the FedEx facility where the scan event occurred.
   */
  scanLocation: ScanLocation;

  /**
   * Location Identification for facilities identified by an alpha numeric location code.
   * Passing Location Id of the Hold at Location (HAL) address is strongly recommended to ensure packages are delivered to the correct address.
   * Example: SEA
   */
  locationId?: string;

  /**
   * This field holds Location Type. If Location Type not available we will get empty value
   */
  locationType?: LocationType;

  /**
   * Field which holds the text description for the exception if the event was an exception.
   * Example: Package available for clearance
   */
  exceptionDescription?: string;

  /**
   * Field which holds the text description of the scan event.
   * Example: 'Picked Up'
   */
  eventDescription: string;

  /**
   * Field which holds the code identifying the type of scan event.
   * Example: 'PU'
   */
  eventType: string;

  /**
   * Field which holds status code of the track information for the scan event.
   * Example: 'PU'
   */
  derivedStatusCode: string;

  /**
   * Field which holds the code identifier for the exception if the event was an exception.
   * Example: A25
   */
  exceptionCode?: string;

  /**
   * Specifies the information about delays.
   */
  delayDetail?: DelayDetail;
}

export interface FedexOutput {
  completeTrackResults: CompleteTrackResult[];
  alerts: Alert[];
}

export interface Alert {
  code: string;
  alertType: "NOTE" | "WARNING";
  message: string;
}

export interface TrackingNumberInfo {
  trackingNumber: string;
  carrierCpde: CarrierCode;
  trackingNumberUniqueId: string;
}

export interface AdditionalTrackingInfo {
  hasAssociatedShipments: boolean;
  nickname: string;
  packageIdentifiers: any;
}

export interface LatestStatusDetail {
  code: string;
  derivedCode: string;
  statusByLocale: string;
  description: string;
  scanLocation: ScanLocation;
  ancillaryDetails: any;
  delayDetail: DelayDetail;
}

export interface DatesAndTimes {
  dateTime: string;
  type: DateTimeType;
}

export interface PackagingDescription {
  type: string;
  description: string;
}

export interface PackageDetails {
  physicalPackagingType: string;
  sequenceNumber: string;
  undeliveredCount: string;
  packagingDescription: PackagingDescription;
  count: string;
  weightAndDimensions: WeightAndDimension;
  packageContent: string[];
  contentPieceCount: string;
  declaredValue: DeclaredValue;
}

export interface DeclaredValue {
  currency: string;
  value: string;
}

export interface Weight {
  unit: string;
  value: string;
}

export interface Dimensions {
  units: string;
  length: string;
  width: string;
  height: string;
}

export interface WeightAndDimension {
  weight: Weight[];
  dimensions: Dimensions[];
}

export interface Window {
  begins?: string;
  ends?: string;
}

export interface StandardTransitTimeWindow {
  description?: string;
  window: Window;
  type?: string;
}

export interface ShipmentDetails {
  weight: Weight[];
}

export interface TrackResult {
  trackingNumberInfo: TrackingNumberInfo;
  additionalTrackingInfo: AdditionalTrackingInfo;
  shipperInformaion: any;
  recipentInformation: any;
  latestStatusDetail?: LatestStatusDetail;
  dateAndTimes: DatesAndTimes[];
  scanEvents: FedexScanEvent[];
  packageDetails: PackageDetails;
  shipmentDetails: ShipmentDetails;
  standardTransitTimeWindow?: StandardTransitTimeWindow;
  specialHandlings: any;
  serviceDetail: any;
}

interface CompleteTrackResult {
  trackingNumber: string;
  trackResults: TrackResult[];
}

export interface FedexResponse {
  transactionId: string;
  output: FedexOutput;
}

interface Expeditiontracking {
  identifier: string;
  deliveryStatus: string;
  trackingCode: string;
}
export interface TrackingResponse {
  expedition: Expeditiontracking;
  fedexResponse: FedexResponse;
}
// #endregion
